import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Cloud, Info, Loader2, LogOut, UploadCloud, X } from 'lucide-react';
import {
  fetchGuestSession,
  GUEST_EVALUATION_TOKEN_KEY,
  submitGuestEvaluation,
  type GuestSessionResponse,
  type GuestSubmitMode
} from '../lib/guestEvaluationsApi';
import { decryptIfEncrypted } from '../utils/encryption';
import { compareNamesByFirstName } from '../utils/playerNameSort';

type MatrixCriterion = {
  id: string;
  label: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getStringAtPath = (source: unknown, path: string): string | null => {
  const parts = path.split('.');
  let current: unknown = source;
  for (const part of parts) {
    if (!isRecord(current) || !(part in current)) return null;
    current = current[part];
  }
  return typeof current === 'string' && current.trim().length > 0 ? current : null;
};

const getFirstString = (source: unknown, paths: string[]): string | null => {
  for (const path of paths) {
    const value = getStringAtPath(source, path);
    if (value) return value;
  }
  return null;
};

const getObjectAtPath = (source: unknown, path: string): Record<string, unknown> | null => {
  const parts = path.split('.');
  let current: unknown = source;
  for (const part of parts) {
    if (!isRecord(current) || !(part in current)) return null;
    current = current[part];
  }
  return isRecord(current) ? current : null;
};

const getFirstObject = (source: unknown, paths: string[]): Record<string, unknown> | null => {
  for (const path of paths) {
    const value = getObjectAtPath(source, path);
    if (value) return value;
  }
  return null;
};

const extractScoresFromSession = (session: unknown): Record<string, unknown> | null => getFirstObject(session, [
  'scores',
  'evaluation.scores',
  'team_evaluation.scores',
  'teams_evaluation.scores',
  'submission.scores',
  'data.scores',
]);

const extractClubIdFromSession = (session: unknown): string | null => getFirstString(session, [
  'distribution.club_id',
  'distribution.team.club_id',
  'club_id',
  'team.club_id',
  'evaluation.club_id',
  'team_evaluation.club_id',
  'teams_evaluation.club_id',
]);

const getEncryptedBlob = (value: unknown): string | null => {
  if (!isRecord(value)) return null;
  if (typeof value.encrypted === 'string') return value.encrypted;
  if (typeof value.ciphertext === 'string') return value.ciphertext;
  return null;
};

const decryptScoresForUi = async (session: unknown): Promise<Record<string, unknown>> => {
  const rawScores = extractScoresFromSession(session);
  if (!rawScores) return {};

  const clubId = extractClubIdFromSession(session) ?? undefined;
  const encryptedBlob = getEncryptedBlob(rawScores);
  if (encryptedBlob && clubId) {
    try {
      const plaintext = await decryptIfEncrypted({ value: encryptedBlob, clubId });
      if (!plaintext) return {};
      const parsed = JSON.parse(plaintext);
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  return rawScores;
};

const parseCriteria = (value: unknown): MatrixCriterion[] => {
  if (!isRecord(value) || !Array.isArray(value.evaluation_spec)) return [];

  return (value.evaluation_spec as unknown[]).reduce<MatrixCriterion[]>((acc, group) => {
    if (!isRecord(group) || !Array.isArray(group.criteria)) return acc;

    (group.criteria as unknown[]).forEach((criterion) => {
      if (!isRecord(criterion)) return;
      const id = typeof criterion.name === 'string' ? criterion.name : '';
      if (!id) return;
      const labelRecord = isRecord(criterion.label) ? criterion.label : null;
      const label = (labelRecord && typeof labelRecord.nl === 'string')
        ? labelRecord.nl
        : (labelRecord && typeof labelRecord.en === 'string')
          ? labelRecord.en
          : id;
      acc.push({ id, label });
    });

    return acc;
  }, []);
};

const parseScoreOptions = (criteria: unknown): number[] => {
  if (!isRecord(criteria) || !isRecord(criteria.meta) || !isRecord(criteria.meta.scoring) || !isRecord(criteria.meta.scoring.scale)) {
    return [0, 1, 2, 3, 4, 5];
  }
  const min = typeof criteria.meta.scoring.scale.min === 'number' ? criteria.meta.scoring.scale.min : 0;
  const max = typeof criteria.meta.scoring.scale.max === 'number' ? criteria.meta.scoring.scale.max : 5;
  if (!Number.isInteger(min) || !Number.isInteger(max) || max < min || max - min > 20) return [0, 1, 2, 3, 4, 5];
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
};

const getPlayerScore = (scores: unknown, playerId: string, criterionId: string): string => {
  if (!isRecord(scores)) return '';
  const playerData = scores[playerId];
  if (!isRecord(playerData)) return '';
  const criteria = isRecord(playerData.criteria) ? playerData.criteria : playerData;
  const raw = criteria[criterionId];
  if (typeof raw === 'number') return String(raw);
  if (isRecord(raw) && typeof raw.score === 'number') return String(raw.score);
  return '';
};

const setPlayerScore = (scores: unknown, playerId: string, criterionId: string, value: string): Record<string, unknown> => {
  const next = isRecord(scores) ? { ...scores } : {};
  const playerDataRaw = next[playerId];
  const playerData = isRecord(playerDataRaw) ? { ...playerDataRaw } : {};
  const criteria = isRecord(playerData.criteria) ? { ...playerData.criteria } : { ...playerData };

  if (!value) {
    delete criteria[criterionId];
  } else {
    criteria[criterionId] = Number(value);
  }

  playerData.criteria = criteria;
  next[playerId] = playerData;
  return next;
};

const getPlayerText = (scores: unknown, playerId: string, key: 'tips' | 'tops'): string => {
  if (!isRecord(scores)) return '';
  const playerData = scores[playerId];
  if (!isRecord(playerData)) return '';
  const raw = playerData[key];
  if (typeof raw === 'string') return raw;
  return '';
};

const setPlayerText = (scores: unknown, playerId: string, key: 'tips' | 'tops', value: string): Record<string, unknown> => {
  const next = isRecord(scores) ? { ...scores } : {};
  const playerDataRaw = next[playerId];
  const playerData = isRecord(playerDataRaw) ? { ...playerDataRaw } : {};
  if (!value.trim()) {
    delete playerData[key];
  } else {
    playerData[key] = value;
  }
  next[playerId] = playerData;
  return next;
};

const isCompletedStatus = (value: string | null): boolean => {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'completed';
};

const isAlreadyCompletedError = (message: string): boolean => {
  const normalized = message.trim().toLowerCase();
  return normalized.includes('already_completed') || normalized.includes('already completed');
};

const buildComparablePlayerData = (
  currentScores: unknown,
  playerId: string,
  criteriaIds: string[]
): Record<string, unknown> => {
  const comparableCriteria = criteriaIds.reduce<Record<string, string>>((acc, criterionId) => {
    const score = getPlayerScore(currentScores, playerId, criterionId);
    if (score !== '') {
      acc[criterionId] = score;
    }
    return acc;
  }, {});

  const comparable: Record<string, unknown> = {
    criteria: comparableCriteria,
  };

  const tops = getPlayerText(currentScores, playerId, 'tops').trim();
  if (tops.length > 0) {
    comparable.tops = tops;
  }

  const tips = getPlayerText(currentScores, playerId, 'tips').trim();
  if (tips.length > 0) {
    comparable.tips = tips;
  }

  return comparable;
};

const EVALUATION_STATUS_PATHS = [
  'evaluation.status',
  'team_evaluation.status',
  'teams_evaluation.status',
  'submission.status',
  'current_evaluation.status',
  'distribution.status',
  'invitee.status',
];

const GUEST_SESSION_TIMING_KEY = 'guestEvaluationSessionTiming';
const SESSION_DURATION_MINUTES = 60;
const SESSION_WARNING_THRESHOLDS = [15, 10, 5, 0];
const SESSION_WARNING_CHECK_INTERVAL_MS = 30000;
const AUTOSAVE_INTERVAL_MS = 3 * 60 * 1000;
const AUTOSAVE_INTERVAL_SECONDS = AUTOSAVE_INTERVAL_MS / 1000;

const formatCountdown = (totalSeconds: number): string => {
  const safeTotal = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeTotal / 60);
  const seconds = safeTotal % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const buildInfoSeenKey = (session: GuestSessionResponse | null): string | null => {
  if (!session) return null;
  const distributionId = session.distribution?.id;
  const inviteeEmail = session.invitee?.email;
  if (!distributionId || !inviteeEmail) return null;
  return `guestEvaluationInfoSeen:${distributionId}:${inviteeEmail.toLowerCase()}`;
};

const GuestEvaluationFill: React.FC = () => {
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState<GuestSessionResponse | null>(null);
  const [evaluationDate, setEvaluationDate] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [savedEvaluationDate, setSavedEvaluationDate] = useState(evaluationDate);
  const [displayPlayers, setDisplayPlayers] = useState<GuestSessionResponse['players']>([]);
  const [scores, setScores] = useState<unknown>({});
  const [teamName, setTeamName] = useState<string>('');
  const [requestedBy, setRequestedBy] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [savingMode, setSavingMode] = useState<GuestSubmitMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [activePlayerIndex, setActivePlayerIndex] = useState(0);
  const [savedScores, setSavedScores] = useState<unknown>({});
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [sessionWarning, setSessionWarning] = useState<string | null>(null);
  const [autosaveCountdownSeconds, setAutosaveCountdownSeconds] = useState(AUTOSAVE_INTERVAL_SECONDS);
  const handleSubmitRef = useRef<(mode: GuestSubmitMode, options?: { silent?: boolean }) => Promise<boolean>>(async () => false);
  const hasUnsavedChangesRef = useRef(false);
  const savingModeRef = useRef<GuestSubmitMode | null>(null);
  const previousRemainingMinutesRef = useRef<number>(SESSION_DURATION_MINUTES + 1);

  const CloudCheckIcon = ({ size = 14 }: { size?: number }) => (
    <span className="relative inline-flex" aria-hidden="true">
      <Cloud size={size} />
      <Check size={Math.max(10, Math.round(size * 0.72))} className="absolute -right-1 -bottom-1 rounded-full bg-white" />
    </span>
  );

  const token = sessionStorage.getItem(GUEST_EVALUATION_TOKEN_KEY);

  const handleLogout = () => {
    sessionStorage.removeItem(GUEST_EVALUATION_TOKEN_KEY);
    navigate('/evaluation-access', { replace: true });
  };

  useEffect(() => {
    const loadSession = async () => {
      if (!token) {
        navigate('/evaluation-access', { replace: true });
        return;
      }
      setLoading(true);
      try {
        setTeamName('');
        setRequestedBy('');
        const data = await fetchGuestSession(token);
        const initialScores = await decryptScoresForUi(data);
        setSessionData(data);
        const initialDate = data.evaluated_at?.slice(0, 10) || new Date().toLocaleDateString('sv-SE');
        setEvaluationDate(initialDate);
        setSavedEvaluationDate(initialDate);
        setDisplayPlayers(data.players ?? []);
        setScores(initialScores);
        setSavedScores(initialScores);
        setError(null);
      } catch (loadError) {
        const message = loadError instanceof Error ? loadError.message : 'Sessie laden mislukt.';
        if (message.includes('401')) {
          sessionStorage.removeItem(GUEST_EVALUATION_TOKEN_KEY);
          navigate('/evaluation-access', { replace: true });
          return;
        }
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    void loadSession();
  }, [navigate, token]);

  useEffect(() => {
    const hydrateDisplayData = async () => {
      const currentSession = sessionData;
      if (!currentSession) {
        setDisplayPlayers([]);
        setTeamName('');
        setRequestedBy('');
        return;
      }
      if (!currentSession?.players?.length) {
        setDisplayPlayers([]);
      }

      const clubId = getFirstString(currentSession, [
        'distribution.club_id',
        'distribution.team.club_id',
        'club_id',
        'team.club_id',
        'evaluation.club_id',
        'team_evaluation.club_id',
        'teams_evaluation.club_id',
      ]) || (currentSession.players ?? []).find((player) => typeof player.club_id === 'string' && player.club_id.trim().length > 0)?.club_id || null;

      const coachNameFromSession = getFirstString(currentSession, [
        'distribution.requested_by_name',
        'requested_by_name',
        'requested_by',
        'requested_by.display_name',
        'requested_by.name',
        'coach.display_name',
        'coach.name',
        'coach_name',
        'coach.email',
        'requested_by_email',
        'distribution.requested_by_email',
      ]);
      if (coachNameFromSession) {
        setRequestedBy(coachNameFromSession);
      } else {
        setRequestedBy('');
      }

      const rawDistributionTeamName = getFirstString(currentSession, [
        'distribution.team_name',
        'team_name',
        'team.name',
        'distribution.team.name',
      ]) || '';
      if (rawDistributionTeamName) {
        const decryptedDistributionTeamName = await decryptIfEncrypted({
          value: rawDistributionTeamName,
          clubId: clubId ?? undefined,
        });
        if (decryptedDistributionTeamName) {
          setTeamName(decryptedDistributionTeamName);
        } else {
          setTeamName(rawDistributionTeamName);
        }
      } else {
        setTeamName('');
      }

      const decryptedPlayers = await Promise.all(
        (currentSession.players ?? []).map(async (player) => {
          const playerClubId = player.club_id || clubId;
          const decryptedName = await decryptIfEncrypted({
            value: player.name,
            clubId: playerClubId ?? undefined,
          });
          return {
            ...player,
            name: decryptedName || player.name,
          };
        })
      );

      const sortedPlayers = [...decryptedPlayers].sort((a, b) => compareNamesByFirstName(a.name, b.name, 'nl-NL'));
      setDisplayPlayers(sortedPlayers);
    };

    void hydrateDisplayData();
  }, [sessionData]);

  const templateName = useMemo(() => {
    if (!sessionData?.meta_evaluation) return '-';
    const translations = sessionData.meta_evaluation.name_translations;
    if (translations?.nl) return translations.nl;
    if (translations?.en) return translations.en;
    return sessionData.meta_evaluation.id;
  }, [sessionData?.meta_evaluation]);

  const requestedByLabel = useMemo(() => {
    if (!sessionData?.distribution) return '-';
    if (requestedBy) return requestedBy;
    const fallbackFromSession = getFirstString(sessionData, [
      'distribution.requested_by_name',
      'requested_by_name',
      'requested_by',
      'coach.display_name',
      'coach_name',
      'requested_by_email',
      'distribution.requested_by_email'
    ]);
    if (fallbackFromSession) return fallbackFromSession;
    return sessionData.distribution.requested_by_name
      || sessionData.distribution.requested_by_email
      || '-';
  }, [requestedBy, sessionData]);

  const teamLabel = useMemo(() => {
    if (!sessionData?.distribution) return '-';
    return teamName
      || '-';
  }, [sessionData?.distribution, teamName]);

  const criteria = useMemo(
    () => parseCriteria(sessionData?.meta_evaluation?.criteria),
    [sessionData?.meta_evaluation?.criteria]
  );
  const scoreOptions = useMemo(
    () => parseScoreOptions(sessionData?.meta_evaluation?.criteria),
    [sessionData?.meta_evaluation?.criteria]
  );
  const activePlayer = displayPlayers[activePlayerIndex] ?? null;
  const criteriaIds = useMemo(() => criteria.map((criterion) => criterion.id), [criteria]);

  const hasUnsavedChangesForPlayer = useCallback((playerId: string): boolean => {
    const currentData = buildComparablePlayerData(scores, playerId, criteriaIds);
    const savedData = buildComparablePlayerData(savedScores, playerId, criteriaIds);
    return JSON.stringify(currentData) !== JSON.stringify(savedData);
  }, [criteriaIds, savedScores, scores]);

  const activePlayerHasUnsavedChanges = useMemo(() => {
    if (!activePlayer) return false;
    return hasUnsavedChangesForPlayer(activePlayer.id);
  }, [activePlayer, hasUnsavedChangesForPlayer]);
  const hasUnsavedChanges = useMemo(
    () => evaluationDate !== savedEvaluationDate || displayPlayers.some((player) => hasUnsavedChangesForPlayer(player.id)),
    [displayPlayers, hasUnsavedChangesForPlayer, evaluationDate, savedEvaluationDate]
  );

  useEffect(() => {
    if (displayPlayers.length === 0) {
      setActivePlayerIndex(0);
      return;
    }
    setActivePlayerIndex((prev) => Math.min(prev, displayPlayers.length - 1));
  }, [displayPlayers]);

  const statusFieldChecks = useMemo(() => {
    if (!sessionData) return [];
    return EVALUATION_STATUS_PATHS.map((path) => {
      const value = getStringAtPath(sessionData, path);
      return {
        path,
        value,
        isCompletedMatch: isCompletedStatus(value),
      };
    });
  }, [sessionData]);

  const completedStatusMatch = useMemo(() => {
    return statusFieldChecks.find((check) => check.isCompletedMatch) ?? null;
  }, [statusFieldChecks]);

  const submissionLocked = useMemo(() => {
    return Boolean(completedStatusMatch);
  }, [completedStatusMatch]);

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    savingModeRef.current = savingMode;
  }, [savingMode]);

  useEffect(() => {
    if (!token) {
      setSessionWarning(null);
      return;
    }

    let startedAtMs = Date.now();
    try {
      const raw = sessionStorage.getItem(GUEST_SESSION_TIMING_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { token?: unknown; startedAt?: unknown };
        if (parsed?.token === token && typeof parsed.startedAt === 'number' && Number.isFinite(parsed.startedAt)) {
          startedAtMs = parsed.startedAt;
        }
      }
    } catch {
      // Ignore invalid session timing cache and recreate it.
    }

    sessionStorage.setItem(GUEST_SESSION_TIMING_KEY, JSON.stringify({
      token,
      startedAt: startedAtMs,
    }));

    const updateSessionWarning = () => {
      const elapsedMinutes = (Date.now() - startedAtMs) / 60000;
      const remainingMinutes = SESSION_DURATION_MINUTES - elapsedMinutes;
      const previousRemaining = previousRemainingMinutesRef.current;
      previousRemainingMinutesRef.current = remainingMinutes;

      let crossedThreshold: number | null = null;
      for (const threshold of SESSION_WARNING_THRESHOLDS) {
        if (previousRemaining > threshold && remainingMinutes <= threshold) {
          crossedThreshold = threshold;
        }
      }
      if (crossedThreshold === null) return;

      const roundedRemaining = Math.max(0, Math.floor(remainingMinutes));
      if (roundedRemaining <= 0) {
        setSessionWarning('Je sessietijd van 60 minuten is bereikt. Opslaan kan mislukken; log opnieuw in met je code.');
      } else {
        setSessionWarning(`Let op: je hebt nog maximaal ${roundedRemaining} minuten om deze evaluatie af te ronden.`);
      }
    };

    previousRemainingMinutesRef.current = SESSION_DURATION_MINUTES + 1;
    updateSessionWarning();
    const timerId = window.setInterval(updateSessionWarning, SESSION_WARNING_CHECK_INTERVAL_MS);
    return () => window.clearInterval(timerId);
  }, [token]);

  useEffect(() => {
    const infoSeenKey = buildInfoSeenKey(sessionData);
    if (!infoSeenKey) return;
    if (submissionLocked) return;

    const alreadySeen = sessionStorage.getItem(infoSeenKey) === '1';
    if (alreadySeen) return;

    setShowInfoModal(true);
    sessionStorage.setItem(infoSeenKey, '1');
  }, [sessionData, submissionLocked]);

  const handleSubmit = useCallback(async (
    mode: GuestSubmitMode,
    options?: { silent?: boolean }
  ): Promise<boolean> => {
    if (!token) return false;
    if (!evaluationDate) {
      setError('Vul een evaluatiedatum in.');
      return false;
    }
    if (submissionLocked) {
      setError(null);
      if (!options?.silent) {
        setSaveSuccess('Deze evaluatie is al ingediend en staat op voltooid.');
      }
      return false;
    }
    if (savingMode !== null) return false;
    setSavingMode(mode);
    if (!options?.silent) {
      setError(null);
      setSaveSuccess(null);
    }
    try {
      const evaluationId = getFirstString(sessionData, [
        'evaluation.id',
        'team_evaluation.id',
        'teams_evaluation.id',
        'current_evaluation.id',
        'existing_evaluation.id',
      ]);
      const distributionId = getFirstString(sessionData, [
        'distribution.id',
        'distribution_id',
      ]);
      const inviteeEmail = getFirstString(sessionData, [
        'invitee.email',
        'email',
      ]);
      const teamId = getFirstString(sessionData, [
        'distribution.team_id',
        'team_id',
      ]);
      const clubId = extractClubIdFromSession(sessionData);
      const inviteeFirstName = sessionData?.invitee?.first_name?.trim() || '';
      const inviteeLastName = sessionData?.invitee?.last_name?.trim() || '';
      const inviteeName = `${inviteeFirstName} ${inviteeLastName}`.trim();
      const evaluatorEmail = inviteeEmail || sessionData?.invitee?.email || '';
      const evaluationTitle = `${sessionData?.distribution?.name || 'Evaluation'} - ${inviteeName || evaluatorEmail || 'Guest'}`;

      await submitGuestEvaluation(token, scores, mode, {
        evaluationId,
        distributionId,
        inviteeEmail,
        teamId,
        clubId,
        evaluationTitle,
        evaluatorEmail,
        evaluatedAt: `${evaluationDate}T12:00:00`,
      });
      const refreshed = await fetchGuestSession(token);
      setSessionData(refreshed);
      setSavedEvaluationDate(evaluationDate);
      const refreshedScores = await decryptScoresForUi(refreshed);
      setScores(refreshedScores);
      setSavedScores(refreshedScores);
      if (!options?.silent) {
        setSaveSuccess(mode === 'draft' ? 'Concept opgeslagen.' : 'Evaluatie definitief ingediend.');
      }
      setLastSavedAt(new Date());
      return true;
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : 'Opslaan mislukt.';
      if (isAlreadyCompletedError(message)) {
        const refreshed = await fetchGuestSession(token);
        setSessionData(refreshed);
        const refreshedScores = await decryptScoresForUi(refreshed);
        setScores(refreshedScores);
        setSavedScores(refreshedScores);
        setError(null);
        if (!options?.silent) {
          setSaveSuccess('Deze evaluatie was al ingediend. Getoonde gegevens zijn geladen.');
        }
        return true;
      } else {
        setError(message);
        return false;
      }
    } finally {
      setSavingMode(null);
    }
  }, [savingMode, scores, sessionData, submissionLocked, token, evaluationDate]);
  handleSubmitRef.current = handleSubmit;

  useEffect(() => {
    if (!token || submissionLocked) return;

    setAutosaveCountdownSeconds(AUTOSAVE_INTERVAL_SECONDS);
    const countdownId = window.setInterval(() => {
      setAutosaveCountdownSeconds((previous) => (
        previous <= 1 ? AUTOSAVE_INTERVAL_SECONDS : previous - 1
      ));
    }, 1000);

    const intervalId = window.setInterval(() => {
      if (savingModeRef.current !== null) return;
      if (!hasUnsavedChangesRef.current) return;
      setAutosaveCountdownSeconds(AUTOSAVE_INTERVAL_SECONDS);
      void handleSubmitRef.current('draft', { silent: true });
    }, AUTOSAVE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      window.clearInterval(countdownId);
    };
  }, [submissionLocked, token]);

  const switchActivePlayer = useCallback(async (nextIndex: number) => {
    const boundedIndex = Math.min(Math.max(nextIndex, 0), Math.max(displayPlayers.length - 1, 0));
    if (boundedIndex === activePlayerIndex) return;

    if (!submissionLocked && activePlayer && hasUnsavedChangesForPlayer(activePlayer.id)) {
      const saved = await handleSubmit('draft', { silent: true });
      if (!saved) return;
    }

    setActivePlayerIndex(boundedIndex);
  }, [
    activePlayer,
    activePlayerIndex,
    displayPlayers.length,
    handleSubmit,
    hasUnsavedChangesForPlayer,
    submissionLocked,
  ]);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-sm text-gray-600">Evaluatie laden...</div>;
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-red-700">
          {error || 'Geen sessie gevonden.'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Evaluatie invullen</h1>
            <label className="mt-3 block text-sm text-gray-700">
              Evaluatiedatum
              <input type="date" required value={evaluationDate} onChange={(event) => setEvaluationDate(event.target.value)} disabled={submissionLocked || savingMode !== null} className="mt-1 block min-h-11 rounded border border-gray-300 px-3 py-2" />
              <span className="mt-1 block text-xs text-gray-500">Kies een datum binnen het teamseizoen. Het invoermoment wordt apart bewaard.</span>
            </label>
            <p className="text-sm text-gray-600">
              {sessionData.distribution.name} · {sessionData.invitee.first_name || sessionData.invitee.email}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowInfoModal(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              aria-label="Informatie"
              title="Informatie"
            >
              <Info size={15} />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              aria-label="Uitloggen"
              title="Uitloggen"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>

        <div className="hidden md:grid md:grid-cols-3 md:gap-2 md:text-xs">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500">Aangevraagd door: </span>
            <span className="font-medium text-gray-800">{requestedByLabel}</span>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500">Team: </span>
            <span className="font-medium text-gray-800">{teamLabel}</span>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
            <span className="text-gray-500">Evaluatie: </span>
            <span className="font-medium text-gray-800">{templateName}</span>
          </div>
        </div>

        {showInfoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
            <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Informatie</h2>
                <button
                  type="button"
                  onClick={() => setShowInfoModal(false)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-1 text-gray-600 hover:bg-gray-50"
                  aria-label="Sluiten"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p>Je bent door {requestedByLabel !== '-' ? requestedByLabel : 'de coach'} gevraagd om deze evaluatie in te vullen.</p>
                <p>
                  Deze aanvraag hoort bij team <strong>{teamLabel}</strong> en evaluatie <strong>{templateName}</strong>.
                </p>
                <p>Vul per speler scores, tops en tips in.</p>
                <p>Op mobiel wordt bij wisselen van speler automatisch tussentijds opgeslagen als er wijzigingen zijn.</p>
                <p>Op desktop (grid-weergave) sla je tussentijds zelf op met de knop <strong>Opslaan als concept</strong>.</p>
                <p>Gebruik onderaan <strong>Definitief indienen</strong> als je klaar bent.</p>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {saveSuccess && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {saveSuccess}
          </div>
        )}
        {sessionWarning && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {sessionWarning}
          </div>
        )}
        {submissionLocked && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            Deze evaluatie is afgerond en kan niet meer worden aangepast.
          </div>
        )}
        <div className="block md:hidden rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
          {activePlayer ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs text-gray-500">
                    Speler {activePlayerIndex + 1} van {displayPlayers.length}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold text-gray-900">{activePlayer.name}</p>
                    {savingMode !== null ? (
                      <span className="inline-flex items-center text-gray-500" title="Bezig met opslaan">
                        <Loader2 size={14} className="animate-spin" />
                      </span>
                    ) : activePlayerHasUnsavedChanges ? (
                      <span className="inline-flex items-center text-amber-600" title="Wijzigingen nog niet opgeslagen">
                        <UploadCloud size={14} />
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-emerald-600" title="Alles opgeslagen">
                        <CloudCheckIcon size={14} />
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { void switchActivePlayer(activePlayerIndex - 1); }}
                    disabled={activePlayerIndex === 0}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-50"
                  >
                    Vorige
                  </button>
                  <button
                    type="button"
                    onClick={() => { void switchActivePlayer(activePlayerIndex + 1); }}
                    disabled={activePlayerIndex === displayPlayers.length - 1}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-50"
                  >
                    Volgende
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {criteria.map((criterion) => {
                  const currentValue = getPlayerScore(scores, activePlayer.id, criterion.id);
                  return (
                    <div key={`${activePlayer.id}-${criterion.id}`} className="rounded-md border border-gray-200 bg-white p-2.5">
                      <p className="mb-2 text-xs font-medium text-gray-700">{criterion.label}</p>
                      {submissionLocked ? (
                        <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700">
                          {currentValue || '-'}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {scoreOptions.map((option) => {
                            const optionValue = String(option);
                            const selected = currentValue === optionValue;
                            return (
                              <button
                                key={`${criterion.id}-${option}`}
                                type="button"
                                onClick={() => setScores((prev: unknown) => setPlayerScore(prev, activePlayer.id, criterion.id, optionValue))}
                                className={`min-w-8 rounded border px-2 py-1 text-xs font-medium ${
                                  selected
                                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                                    : 'border-gray-300 bg-white text-gray-700'
                                }`}
                              >
                                {option}
                              </button>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => setScores((prev: unknown) => setPlayerScore(prev, activePlayer.id, criterion.id, ''))}
                            className={`rounded border px-2 py-1 text-xs font-medium ${
                              currentValue === ''
                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                : 'border-gray-300 bg-white text-gray-700'
                            }`}
                          >
                            -
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Tops</label>
                  {submissionLocked ? (
                    <div className="min-h-7 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                      {getPlayerText(scores, activePlayer.id, 'tops') || '-'}
                    </div>
                  ) : (
                    <input
                      value={getPlayerText(scores, activePlayer.id, 'tops')}
                      onChange={(event) => setScores((prev: unknown) => setPlayerText(prev, activePlayer.id, 'tops', event.target.value))}
                      disabled={submissionLocked}
                      className="h-9 w-full rounded border border-gray-300 px-2 text-sm focus:border-orange-500 focus:outline-none"
                    />
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Tips</label>
                  {submissionLocked ? (
                    <div className="min-h-7 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                      {getPlayerText(scores, activePlayer.id, 'tips') || '-'}
                    </div>
                  ) : (
                    <input
                      value={getPlayerText(scores, activePlayer.id, 'tips')}
                      onChange={(event) => setScores((prev: unknown) => setPlayerText(prev, activePlayer.id, 'tips', event.target.value))}
                      disabled={submissionLocked}
                      className="h-9 w-full rounded border border-gray-300 px-2 text-sm focus:border-orange-500 focus:outline-none"
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-xs text-gray-600">Geen spelers gevonden.</div>
          )}
        </div>

        <div className="hidden md:block overflow-auto border border-gray-200 rounded-md">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 sticky left-0 bg-gray-50 border-b border-gray-200">Speler</th>
                {criteria.map((criterion) => (
                  <th key={criterion.id} className="px-2 py-1.5 text-center font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                    {criterion.label}
                  </th>
                ))}
                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-200">Tops</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-200">Tips</th>
              </tr>
            </thead>
            <tbody>
              {displayPlayers.map((player) => (
                <tr key={player.id} className="border-b border-gray-100">
                  <td className="px-2 py-1.5 text-gray-800 sticky left-0 bg-white whitespace-nowrap">{player.name}</td>
                  {criteria.map((criterion) => (
                    <td key={`${player.id}-${criterion.id}`} className="px-2 py-1.5 text-center">
                      {submissionLocked ? (
                        <span className="inline-block min-w-[56px] rounded border border-gray-200 bg-gray-50 px-2 py-1 text-center text-gray-700">
                          {getPlayerScore(scores, player.id, criterion.id) || '-'}
                        </span>
                      ) : (
                        <select
                          value={getPlayerScore(scores, player.id, criterion.id)}
                          onChange={(event) => setScores((prev: unknown) => setPlayerScore(prev, player.id, criterion.id, event.target.value))}
                          disabled={submissionLocked}
                          className="h-7 min-w-[56px] rounded border border-gray-300 px-1 text-xs text-center focus:border-orange-500 focus:outline-none"
                        >
                          <option value="">-</option>
                          {scoreOptions.map((option) => (
                            <option key={`${criterion.id}-${option}`} value={option}>{option}</option>
                          ))}
                        </select>
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 min-w-[120px]">
                    {submissionLocked ? (
                      <div className="min-h-7 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                        {getPlayerText(scores, player.id, 'tops') || '-'}
                      </div>
                    ) : (
                      <input
                        value={getPlayerText(scores, player.id, 'tops')}
                        onChange={(event) => setScores((prev: unknown) => setPlayerText(prev, player.id, 'tops', event.target.value))}
                        disabled={submissionLocked}
                        className="h-7 w-full rounded border border-gray-300 px-2 text-xs focus:border-orange-500 focus:outline-none"
                      />
                    )}
                  </td>
                  <td className="px-2 py-1.5 min-w-[120px]">
                    {submissionLocked ? (
                      <div className="min-h-7 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                        {getPlayerText(scores, player.id, 'tips') || '-'}
                      </div>
                    ) : (
                      <input
                        value={getPlayerText(scores, player.id, 'tips')}
                        onChange={(event) => setScores((prev: unknown) => setPlayerText(prev, player.id, 'tips', event.target.value))}
                        disabled={submissionLocked}
                        className="h-7 w-full rounded border border-gray-300 px-2 text-xs focus:border-orange-500 focus:outline-none"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:gap-3">
            {lastSavedAt && (
              <span className="text-xs text-gray-500">
                Laatst opgeslagen om {lastSavedAt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {!submissionLocked && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                {savingMode !== null ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Opslaan
                  </>
                ) : activePlayerHasUnsavedChanges ? (
                  <>
                    <UploadCloud size={12} />
                    Niet opgeslagen
                    <span className="hidden md:inline">· autosave over {formatCountdown(autosaveCountdownSeconds)}</span>
                  </>
                ) : (
                  <>
                    <CloudCheckIcon size={12} />
                    Opgeslagen
                    <span className="hidden md:inline">· autosave over {formatCountdown(autosaveCountdownSeconds)}</span>
                  </>
                )}
              </span>
            )}
            <button
              type="button"
              onClick={() => { void handleSubmit('draft'); }}
              disabled={savingMode !== null || submissionLocked}
              className="hidden md:inline-flex md:w-auto items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
            >
              {savingMode === 'draft' ? 'Opslaan...' : 'Opslaan als concept'}
            </button>
            <button
              type="button"
              onClick={() => { void handleSubmit('submit'); }}
              disabled={savingMode !== null || submissionLocked}
              className="inline-flex w-full items-center justify-center rounded-md bg-primary-600 text-white px-3 py-2 text-sm font-medium hover:bg-primary-700 disabled:opacity-60 md:w-auto"
            >
              {submissionLocked ? 'Al ingediend' : savingMode === 'submit' ? 'Indienen...' : 'Definitief indienen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestEvaluationFill;
